import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { ActionButton } from 'app/components/Button/ActionButton';
import { FormInput } from 'app/components/Input/FormInput';
import { FormTextarea } from 'app/components/Input/FormTextarea';
import { FormSelect } from 'app/components/Select/FormSelect';
import { stringifyRequestQuery } from 'app/components/Table/core/helpers';
import { LoadingCard } from 'app/components/Table/loading/LoadingCard';
import Typography from 'app/components/Typography';
import RootContentPage from 'app/components/layout/RootContentPage';

import { useAuth } from 'app/modules/auth';
import { DECIMAL_REGEX, MAX_WEIGHT, MIN_WEIGHT } from 'app/modules/inventory/items/core/constants';
import { getSetting } from 'app/modules/system-settings/configuration/core/request';
import { SettingType } from 'app/modules/system-settings/configuration/core/type';

import { blockInvalidCharacters, compileFormData, trimObject } from 'app/helpers';
import { getResponseError } from 'app/helpers/errors';
import { useNavigateWithPreviousPath } from 'app/helpers/utils';
import { useBoolean, useNumber, useString } from 'app/hooks';
import { usePolicyInUser } from 'app/hooks/policy';
import { isNil, isNumber, isString } from 'lodash';

import { PATH } from 'app/constants/path';

import { StockTypeEnum, WeightUnitsEnum } from '../core/enum';
import { createItem, getItem, updateItem } from '../core/requests';
import { ItemRequest, QtyUnit, initialItemRequest } from '../core/type';
import AddImageItemCard from './AddImageItemCard';
import StockTypeCard from './StockTypeCard';
import { useFormik } from 'formik';
import { useOrganizationStore } from 'theme/layout/components/aside/store';
import * as Yup from 'yup';

const Main = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 28px 28px 8px 28px;
  .action-btn {
    margin-top: 0px !important;
  }
`;
const Header = styled.div`
  .action-btn {
    margin-top: 0px !important;
  }
`;
const ItemType = styled.div`
  font-weight: 700;
  font-size: 14px;
  line-height: 23px;
  color: var(--gray-700);
`;
interface Props {
  itemId?: number;
}

const BodyFormItem: React.FC<Props> = ({ itemId }) => {
  const isLoading = useBoolean();

  const { data: expiriedItem, isLoading: isLoadingExpiriedItem } = useQuery({
    queryKey: ['expiriedItem'],
    queryFn: () => getSetting(SettingType.defaultShelfLife),
  });

  const [formRequest] = useState<{
    name: string;
    info: string;
    skuId: string;
    skuSub: string;
    weight: number;
    volumePerPackage: number | null;
    // shelfLife: number;
    shelfLifeAlertAfter: number;
    productNumber: number | null;
  }>({
    name: initialItemRequest.name,
    info: initialItemRequest.info,
    skuId: initialItemRequest.skuId,
    skuSub: initialItemRequest.skuSub,
    weight: initialItemRequest.weight,
    volumePerPackage: !itemId ? initialItemRequest.volumePerPackage : null,
    // shelfLife: initialItemRequest.shelfLifeAlertAfter,
    shelfLifeAlertAfter: initialItemRequest.shelfLifeAlertAfter,
    productNumber: initialItemRequest.productNumber,
  });
  const [file, setFile] = useState<File | null>(null);
  const stockType = useNumber(itemId ? -1 : StockTypeEnum['Non-Chargeable']);
  const qtyUnit = useNumber(0);
  const weightUnit = useNumber(WeightUnitsEnum.g);
  const isView = useBoolean(false);
  const isRemoveImage = useBoolean();
  const originImage = useString('');
  const [quantityUnits, setQuantityUnits] = useState<QtyUnit[]>([]);
  const navigate = useNavigate();
  const navigateWithPreviousPath = useNavigateWithPreviousPath();
  const originalStockType = useNumber(-1);
  const isItemAssigned = useBoolean(false);

  const { currentUser, setCurrentUser } = useAuth();
  const permission = usePolicyInUser(currentUser?.policy ?? []);
  const isSystemAdminCurrent = permission.systemAdmin;
  const organization = useOrganizationStore((state) => state.state.organization);

  const orgSetting = useMemo(
    () => expiriedItem?.find((item) => item.organization?.id === organization?.id),
    [expiriedItem, organization],
  );
  const defaultSetting = useMemo(
    () => expiriedItem?.find((item) => !item.organization),
    [expiriedItem],
  );

  useEffect(() => {
    if (!itemId) {
      getQuantityUnits();
      return;
    }
    getDataItem(Number(itemId));
  }, [itemId]);

  const getDataItem = async (idItem: number) => {
    try {
      isLoading.setValue(true);
      const datRes = await getItem(
        stringifyRequestQuery({
          page: 1,
          limit: 10,
          orderBy: 'ward',
          orderDirection: 'ASC',
        }),
        idItem,
      );
      if (datRes) {
        formik.setValues({
          info: datRes.info,
          name: datRes.name,
          skuId: datRes.skuId,
          skuSub: datRes.skuSub,
          weight: datRes.weight,
          volumePerPackage: datRes.volumePerPackage || null,
          // shelfLife: datRes.shelfLife || 0,
          shelfLifeAlertAfter: datRes.shelfLifeAlertAfter || 0,
          productNumber: datRes.productNumber,
        });
        originImage.setValue(datRes.image);
        stockType.setValue(Number(datRes.stockType));
        isItemAssigned.setValue(datRes.hasBin);
        originalStockType.setValue(Number(datRes.stockType));
        await getQuantityUnits(datRes.qtyUnit.id);
      }
    } catch (error) {
      navigate(PATH.ITEMS);
    } finally {
      isLoading.setValue(false);
    }
  };

  const getQuantityUnits = async (newQtyUnit?: number) => {
    isLoading.setValue(true);
    const res = await getSetting(SettingType.quantityUnit);
    setQuantityUnits(
      res.map((el) => ({
        title: el.name,
        value: el.id!,
      })),
    );
    qtyUnit.setValue(newQtyUnit ? newQtyUnit : qtyUnit.value);
    isLoading.setValue(false);
  };

  const redirectItemDetailPage = (idItem: number) => {
    navigateWithPreviousPath(PATH.ITEMS_DETAIL.replace(':id', String(idItem)));
  };

  const defaultShelfLife = orgSetting?.value
    ? Number(orgSetting.value)
    : Number(defaultSetting?.value) || 90;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...formRequest,
      // shelfLife: defaultShelfLife,
      shelfLifeAlertAfter: defaultShelfLife,
    },
    validationSchema: Yup.object().shape({
      name: Yup.string()
        .trim()
        .min(3, 'Minimum 3 symbols')
        .max(50, 'Maximum 50 symbols')
        .required('Item name is required'),
      skuId: Yup.string().trim().max(30, 'Maximum 30 symbols').required('SKU is required'),
      skuSub: Yup.string().trim().max(30, 'Maximum 30 symbols'),
      weight: Yup.string()
        .matches(DECIMAL_REGEX, 'Maximum 4 decimal places allowed')
        .test('is-valid-number', 'Must be a valid number', (val) => !isNaN(Number(val)))
        .test('min', 'Minimum weight is 0.001g', (val) => Number(val) >= MIN_WEIGHT)
        .test('max', 'Maximum weight is 5000g', (val) => Number(val) <= MAX_WEIGHT)
        .required('Weight is required'),
      volumePerPackage: Yup.number().min(1, 'Minimum quantity = 1'),
      info: Yup.string().trim(),
      // shelfLife: Yup.number()
      //   .min(0, 'Shelf life cannot be a negative number')
      //   .required('Shelf life is required')
      //   .when('shelfLifeAlertAfter', (alertAfter: number, schema: any) => {
      //     if (isNumber(alertAfter)) {
      //       return schema.min(
      //         alertAfter,
      //         'Shelf life must be greater than or equal to Shelf life alert after',
      //       );
      //     }
      //     return schema;
      //   })
      //   .nullable(),
      shelfLifeAlertAfter: Yup.number()
        .nullable()
        .min(0, 'Shelf life alert after cannot be negative'),
      productNumber: Yup.mixed()
        .test(
          'is-string-or-number',
          'productNumber must be a string or number',
          (value) => isString(value) || isNumber(value) || isNil(value),
        )
        .nullable(),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const trimmedValues = trimObject(values) as ItemRequest;
      const request: ItemRequest = {
        ...trimmedValues,
        weight: Number(trimmedValues.weight),
        stockType: stockType.value,
        weightUnit: weightUnit.value,
        volumePerPackage: Number(values.volumePerPackage || 1),
        qtyUnit: qtyUnit.value,
        // shelfLife: Number(trimmedValues.shelfLife) || 0,
        shelfLifeAlertAfter: Number(trimmedValues.shelfLifeAlertAfter) || 0,
        productNumber: Number(trimmedValues.productNumber) || null,
      };
      if (file) {
        request.image = file;
      }
      if (isSystemAdminCurrent) {
        request.orgId = organization?.id;
      }

      try {
        if (itemId) {
          if (isRemoveImage.value) {
            request.removeImage = true;
          }
          const dataEditRes = await updateItem(itemId, compileFormData(request));
          toast.success(`Update Item successfully!`);
          if (dataEditRes) {
            redirectItemDetailPage(Number(dataEditRes.id));
          }
          return;
        }
        const dataAddRes = await createItem(compileFormData(request));
        toast.success(`Add Item successfully!`);
        if (dataAddRes) {
          redirectItemDetailPage(Number(dataAddRes.id));
        }
      } catch (error: any) {
        getResponseError(error);
      } finally {
        setSubmitting(true);
      }
    },
  });

  const onCancel = () => {
    if (itemId) {
      return redirectItemDetailPage(Number(itemId));
    }
    navigate(PATH.ITEMS);
  };

  const renderLabelButtonSubmit = () => {
    if (itemId) {
      return isView.value ? 'Edit' : 'Save';
    }
    return 'Add';
  };
  const renderButton = () => {
    return (
      <ActionButton
        customClass="action-btn"
        onCancel={onCancel}
        disableButton={
          formik.isSubmitting ||
          !formik.isValid ||
          !formik.values.name ||
          !formik.values.skuId ||
          !formik.touched ||
          !qtyUnit.value
        }
        isSubmitting={formik.isSubmitting}
        labelButtonSubmit={renderLabelButtonSubmit()}
      />
    );
  };

  const formTitle = itemId ? `Edit item` : 'Add item ';

  return (
    <form id="kt_modal_form_item" onSubmit={formik.handleSubmit} noValidate>
      <RootContentPage
        title=""
        header={
          <Header className="d-flex justify-content-between align-items-center">
            <Typography variant="heading-6">{`${formTitle}`}</Typography>
            {!itemId && renderButton()}
          </Header>
        }
      >
        <Main>
          <div
            className="d-flex justify-content-between align-items-center"
            style={{
              width: '100%',
            }}
          >
            <Typography variant="title-1" lineHeight={32}>
              Detail
            </Typography>
            {itemId && renderButton()}
          </div>

          <div
            className="d-flex flex-column"
            style={{
              width: '75%',
              marginTop: 32,
            }}
          >
            <div className="d-flex">
              <AddImageItemCard
                file={file}
                setFile={setFile}
                image={originImage.value}
                isDisable={isView.value}
                isRemoveImage={isRemoveImage.value}
                setIsRemoveImage={isRemoveImage.setValue}
              />
              <div className="d-flex flex-column justify-content-between w-100 ps-10 position-relative">
                <FormInput
                  label="Item name"
                  placeholder="Item name"
                  formik={formik}
                  field="name"
                  isRequired
                  rootClass="w-100"
                  isDisable={isView.value}
                />
                <div>
                  <ItemType>Item type</ItemType>
                  <StockTypeCard
                    stockType={stockType}
                    isDisable={isView.value}
                    isInItemForm={true}
                  />
                </div>
                <div
                  className="d-flex justify-content-between"
                  style={{
                    marginTop: 24,
                  }}
                >
                  <div className="w-50 pe-3">
                    <FormInput
                      label="SKU"
                      placeholder="SKU"
                      formik={formik}
                      field="skuId"
                      isRequired
                      rootClass="w-100"
                      isDisable={isView.value}
                    />
                  </div>
                  <div className="w-50 ps-3">
                    <FormInput
                      label="Material Code"
                      placeholder="Material Code"
                      formik={formik}
                      field="skuSub"
                      isDisable={isView.value}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-between">
                  <div className="w-50 pe-3">
                    <FormSelect
                      label="Unit"
                      isRequired
                      optionsSelect={quantityUnits}
                      activeSelect={qtyUnit.value}
                      handleChange={(select) => qtyUnit.setValue(select.value)}
                      fieldActive="value"
                      fieldSelect="title"
                      styleSelectCard={{
                        width: 277,
                      }}
                      isDisable={isView.value}
                      inputPlaceholder="Select unit"
                      extraLabelClass="mb-0"
                    />
                  </div>
                  <div className="w-50 ps-3">
                    <FormInput
                      label="Weight (g)"
                      placeholder="Weight"
                      formik={formik}
                      field="weight"
                      typeInput="number"
                      onKeyDown={(e) => blockInvalidCharacters(e, 'float')}
                      isRequired
                      rootClass="w-100"
                      isDisable={isView.value}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-between">
                  <div className="w-50 pe-3">
                    <FormInput
                      label="Volume per package"
                      placeholder="Volume per package"
                      formik={formik}
                      field="volumePerPackage"
                      typeInput="number"
                      onKeyDown={(e) => blockInvalidCharacters(e, 'integer')}
                      rootClass="w-100"
                      isDisable={isView.value}
                    />
                  </div>
                  <div className="w-50 ps-3">
                    <FormInput
                      typeInput="number"
                      label="Product number"
                      placeholder="Product number"
                      onKeyDown={(e) => blockInvalidCharacters(e, 'integer')}
                      formik={formik}
                      field="productNumber"
                      isDisable={isView.value}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-between">
                  {/* <div className="w-50 pe-3">
                    <FormInput
                      typeInput="number"
                      label="Shelf life (days)"
                      placeholder="Shelf life (days)"
                      formik={formik}
                      field="shelfLife"
                      isDisable={isView.value}
                    />
                  </div> */}
                  <div className="w-50 pe-3">
                    <FormInput
                      typeInput="number"
                      label="Days before expiration"
                      placeholder="Days before expiration"
                      formik={formik}
                      field="shelfLifeAlertAfter"
                      isDisable={isView.value}
                    />
                  </div>
                </div>

                <FormTextarea
                  label="Description"
                  placeholder="Description about this item"
                  formik={formik}
                  field="info"
                  maxLength={200}
                  showCount
                  isDisable={isView.value}
                />
              </div>
            </div>
          </div>
        </Main>

        {(isLoading.value || isLoadingExpiriedItem) && <LoadingCard />}
      </RootContentPage>
    </form>
  );
};
export default BodyFormItem;
